/**
 * Mapa central de íconos de marca de agua por ruta y por sección para el cintillo (.page-banner).
 * Todos los íconos provienen de 'lucide-react' (misma familia outline, proporciones y grosor de trazo uniforme).
 * Cada página del menú cuenta con un ícono único y exclusivo que representa su contenido.
 */
import {
  // Acerca de ITE
  Target,
  History,
  Scale,
  Network,
  Landmark,

  // Consejo General
  UserCheck,
  Layers,
  Briefcase,
  Users,

  // Acuerdos
  Files,
  FileCheck,
  ScrollText,
  FileSignature,

  // Demarcaciones
  FolderCheck,
  Map as MapIcon,
  ShieldCheck,
  MapPin,

  // Transparencia
  FileSearch,
  BarChart3,
  MessageCircleQuestion,
  Database,

  // Comunicación Social
  Newspaper,
  Radio,
  Podcast,
  Video,
  Megaphone,

  // Cultura Cívica
  Gamepad2,
  School,
  Baby,
  GraduationCap,
  HeartHandshake,
  Vote,

  // Archivo
  BookMarked,
  CalendarRange,
  Boxes,
  Archive,

  // Licitaciones & Encuesta
  Gavel,
  ClipboardCheck,

  // Complementarias & Genérico
  BookOpen,
  BookUser,
  FileText,
} from "lucide-react";

/**
 * 1. Mapa directo por ruta exacta (en minúsculas sin barra diagonal al final)
 */
export const ROUTE_WATERMARK_MAP = {
  // --- Acerca de ITE ---
  "/finesinstitucionales": Target,
  "/historia": History,
  "/principiosrectores": Scale,
  "/estructuraorganica": Network,

  // --- Consejo General ---
  "/integracion": UserCheck,
  "/comisiones": Layers,
  "/jge": Briefcase,

  // --- Acuerdos ---
  "/acuerdosanteriores": Files,
  "/acuerdosine": ScrollText,
  "/acuerdostableine": ScrollText,
  "/acuerdostableite": FileCheck,

  // --- Demarcaciones ---
  "/acuerdos": FolderCheck,
  "/mapas": MapIcon,
  "/acuerdospj": ShieldCheck,
  "/mapaspj": MapPin,
  "/distritacion": MapIcon,

  // --- Transparencia ---
  "/transparencia": FileSearch,
  "/infografias": BarChart3,
  "/preguntasfrecuentes": MessageCircleQuestion,
  "/datosabiertos": Database,

  // --- Comunicación Social ---
  "/iteconecta": Podcast,
  "/encorto": Video,
  "/galeria": Video,

  // --- Cultura Cívica ---
  "/actividadesludicas": Gamepad2,
  "/eleccionesescolares": School,
  "/parlamentoinfantil": Baby,
  "/parlamentojuvenil": GraduationCap,
  "/valoresdemocracia": HeartHandshake,

  // --- Archivo ---
  "/instrumentosnormativos": BookMarked,
  "/planeacionarchivistica": CalendarRange,
  "/instrumentosarchivisticos": Boxes,
  "/inventariosdocumentales": Boxes,
  "/infografiasarchivo": BookMarked,

  // --- Licitaciones & Encuesta ---
  "/licitaciones": Gavel,
  "/encuesta": ClipboardCheck,
  "/encuestasatisfaccion": ClipboardCheck,

  // --- Secciones complementarias ---
  "/estradoselectronicos": ScrollText,
  "/directorio": BookUser,
  "/directoriopage": BookUser,
  "/memoriaselectorales": BookOpen,
  "/memoriaelectoral20202021": BookOpen,
  "/oic": ShieldCheck,
  "/acuerdosoic": ShieldCheck,
  "/galeriaoic": ShieldCheck,
  "/convocatoriasapartado": Megaphone,
};

/**
 * 2. Mapa de respaldo por clave de sección (cuando la subpágina no tiene ícono propio asignado)
 */
export const SECTION_WATERMARK_MAP = {
  "acerca-ite": Landmark,
  "consejo-general": Users,
  "acuerdos": FileCheck,
  "demarcaciones": MapIcon,
  "transparencia": FileSearch,
  "comunicacion-social": Megaphone,
  "cultura-civica": Vote,
  "archivo": Archive,
  "licitaciones": Gavel,
  "encuesta-satisfaccion": ClipboardCheck,
  "procesos-electorales": Vote,
  "oic": ShieldCheck,
  "estrados-electronicos": ScrollText,
  "memorias-electorales": BookOpen,
  "convocatorias": Megaphone,
  "directorio": BookUser,
};

/**
 * Ícono genérico del sitio (para páginas no catalogadas)
 */
export const DEFAULT_WATERMARK_ICON = FileText;

/**
 * Resuelve el ícono de marca de agua óptimo para la ruta y sección actual.
 *
 * @param {string} pathname - Ruta actual de la página (ej. "/FinesInstitucionales")
 * @param {string} sectionKey - Clave de la sección (ej. "acerca-ite")
 * @param {string} title - Título actual de la página (ej. "Fines Institucionales")
 * @param {React.ComponentType|string|null} customIcon - Ícono explícito pasado por prop
 * @returns {React.ComponentType} Componente de ícono Lucide React
 */
export const getWatermarkIconForRoute = (pathname = "", sectionKey = "", title = "", customIcon = null) => {
  // 1. Sobrescritura directa por prop
  if (customIcon) {
    if (typeof customIcon === "function" || typeof customIcon === "object") {
      return customIcon;
    }
  }

  const cleanPath = (pathname || "").toLowerCase().trim().replace(/\/+$/, "") || "/";
  const cleanTitle = (title || "").toLowerCase().trim();

  // 2. Coincidencia exacta en el mapa de rutas
  if (ROUTE_WATERMARK_MAP[cleanPath]) {
    return ROUTE_WATERMARK_MAP[cleanPath];
  }

  // 3. Coincidencia por patrones dinámicos en la ruta (rutas anuales o paramétricas)
  // Acuerdos INE por año (ej. /AcuerdosINE2026, /AcuerdosINE2025...)
  if (cleanPath.startsWith("/acuerdosine")) {
    return FileSignature;
  }

  // Acuerdos ITE por año (ej. /Acuerdos2026, /Acuerdos2025, /Acuerdos1998...)
  if (cleanPath.startsWith("/acuerdos20") || cleanPath.startsWith("/acuerdos19")) {
    return FileCheck;
  }

  // Boletines por año (ej. /Boletines2026, /Boletines2025...)
  if (cleanPath.startsWith("/boletines")) {
    return Newspaper;
  }

  // Monitoreos por año o proceso (ej. /Monitoreos2025, /MonitoreosPELE2024...)
  if (cleanPath.startsWith("/monitoreos")) {
    return Radio;
  }

  // Procesos electorales (ej. /pe20232024, /pepj20242025...)
  if (cleanPath.startsWith("/pe20") || cleanPath.startsWith("/pee20") || cleanPath.startsWith("/procesos")) {
    return Vote;
  }

  // Subpáginas de Transparencia (artículos, obligaciones, formatos, convenios, avisos)
  if (
    cleanPath.startsWith("/articulo") ||
    cleanPath.startsWith("/art") ||
    cleanPath.startsWith("/obligaciones") ||
    cleanPath.startsWith("/tablaa") ||
    cleanPath.startsWith("/poa") ||
    cleanPath.startsWith("/convenios") ||
    cleanPath.startsWith("/avisos") ||
    cleanPath.startsWith("/informes") ||
    cleanPath.startsWith("/planes") ||
    cleanPath.startsWith("/formatosolicitud")
  ) {
    return FileSearch;
  }

  // 4. Coincidencia semántica en título para casos dinámicos
  if (cleanTitle.includes("fines institucionales")) return Target;
  if (cleanTitle.includes("historia")) return History;
  if (cleanTitle.includes("principios rectores")) return Scale;
  if (cleanTitle.includes("estructura orgánica") || cleanTitle.includes("estructura organica")) return Network;
  if (cleanTitle.includes("integración") || cleanTitle.includes("integracion")) return UserCheck;
  if (cleanTitle.includes("comisiones")) return Layers;
  if (cleanTitle.includes("junta general ejecutiva") || cleanTitle === "jge") return Briefcase;
  if (cleanTitle.includes("infografía") || cleanTitle.includes("infografia")) return BarChart3;
  if (cleanTitle.includes("preguntas frecuentes")) return MessageCircleQuestion;
  if (cleanTitle.includes("datos abiertos")) return Database;
  if (cleanTitle.includes("boletín") || cleanTitle.includes("boletin")) return Newspaper;
  if (cleanTitle.includes("monitoreo")) return Radio;
  if (cleanTitle.includes("ite conecta")) return Podcast;
  if (cleanTitle.includes("en corto")) return Video;
  if (cleanTitle.includes("actividades lúdicas") || cleanTitle.includes("actividades ludicas")) return Gamepad2;
  if (cleanTitle.includes("elecciones escolares")) return School;
  if (cleanTitle.includes("parlamento infantil")) return Baby;
  if (cleanTitle.includes("parlamento juvenil")) return GraduationCap;
  if (cleanTitle.includes("valores de la democracia") || cleanTitle.includes("valores")) return HeartHandshake;
  if (cleanTitle.includes("instrumentos normativos")) return BookMarked;
  if (cleanTitle.includes("planeación archivística") || cleanTitle.includes("planeacion archivistica")) return CalendarRange;
  if (cleanTitle.includes("instrumentos archivísticos") || cleanTitle.includes("instrumentos archivisticos")) return Boxes;
  if (cleanTitle.includes("inventarios documentales")) return Boxes;
  if (cleanTitle.includes("licitaciones") || cleanTitle.includes("licitación") || cleanTitle.includes("licitacion")) return Gavel;
  if (cleanTitle.includes("encuesta") || cleanTitle.includes("satisfacción") || cleanTitle.includes("satisfaccion")) return ClipboardCheck;

  // 5. Fallback por clave de sección
  const normSectionKey = (sectionKey || "").toLowerCase().trim().replace(/[\s_]+/g, "-");
  if (SECTION_WATERMARK_MAP[normSectionKey]) {
    return SECTION_WATERMARK_MAP[normSectionKey];
  }

  // 6. Fallback genérico institucional
  return DEFAULT_WATERMARK_ICON;
};

export default getWatermarkIconForRoute;
